import React, { useCallback, useEffect, useState } from 'react';

import { format, parseISO } from 'date-fns';

import { Row, Col, Button } from 'react-bootstrap';
import { FaTrashAlt, FaReply } from 'react-icons/fa';

import { ContainerDados } from './styles';

import CadastroCertificado from './cadastro';
import getResponseErrors from '../../../utils/getResponseErrors';
import api from '../../../services/api';
import Loading from '../../../components/Loading';
import { useToast } from '../../../hooks/toast';
import { useAuth } from '../../../hooks/auth';
import { TIPO_USUARIO } from '../../../utils/funcoes';

export interface ICertificados {
  CerCodigo: number;
  CerUsuCodigo: number;
  CerDataValidade: Date;
  CerArquivo: string;
  dataValidadeFormatada?: string;
}

type CertificadoProps = {
  usuCodigo: number;
};

const Certificado: React.FC<CertificadoProps> = ({ usuCodigo }) => {
  const { addToast } = useToast();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showCadastro, setShowCadastro] = useState(false);
  const [certificado, setCertificado] = useState<ICertificados[]>();

  useEffect(() => {
    async function loadDados(): Promise<void> {
      setLoading(true);
      try {
        const response = await api.get<ICertificados[]>(
          `/medicos/certificados/${usuCodigo}`,
        );

        const dadosFormatados = response.data.map((cert: ICertificados) => {
          return {
            ...cert,
            dataValidadeFormatada: format(
              parseISO(cert.CerDataValidade.toString()),
              'dd/MM/yyyy HH:mm',
            ),
          };
        });
        setCertificado(dadosFormatados);
      } catch (error) {
        const mensagemErro = getResponseErrors(error);
        addToast({
          title: 'Não foi possível buscar dados',
          type: 'error',
          description: mensagemErro,
        });
      } finally {
        setLoading(false);
      }
    }
    if (usuCodigo && usuCodigo > 0) {
      loadDados();
    }
  }, [addToast, usuCodigo]);

  const handleDelete = useCallback(
    async (codigo: number) => {
      setLoading(true);
      try {
        await api.delete(`/medicos/certificado/${codigo}`);

        setCertificado(state => {
          return state?.filter(cert => cert.CerCodigo !== codigo);
        });
      } catch (error) {
        const mensagemErro = getResponseErrors(error);
        addToast({
          title: 'Não foi possível excluir o registro',
          type: 'error',
          description: mensagemErro,
        });
      } finally {
        setLoading(false);
      }
    },
    [addToast],
  );

  const handleRegistroIncluido = useCallback(
    async (dados: ICertificados) => {
      try {
        const dadosFormatados: ICertificados = {
          ...dados,
          dataValidadeFormatada: format(
            parseISO(dados.CerDataValidade.toString()),
            'dd/MM/yyyy HH:mm',
          ),
        };

        setCertificado(state => state?.concat(dadosFormatados));

        setShowCadastro(false);
      } catch (error) {
        const mensagemErro = getResponseErrors(error);
        addToast({
          title: 'Não foi possível incluir o registro',
          type: 'error',
          description: mensagemErro,
        });
      }
    },
    [addToast],
  );

  return (
    <>
      {loading && <Loading backgroundBlack={false} />}
      <ContainerDados className="rounded">
        {showCadastro ? (
          <>
            <Button
              type="button"
              variant="outline-primary"
              size="sm"
              className="my-2"
              onClick={() => {
                setShowCadastro(false);
              }}
            >
              <FaReply size={16} /> Listar Certificado
            </Button>
            <CadastroCertificado
              usuCodigo={usuCodigo}
              handleDadosSalvos={handleRegistroIncluido}
            />
          </>
        ) : (
          <Row>
            <Col>
              <div className="d-flex justify-content-between aling-items-center my-2">
                <h5>Certificados</h5>
                <Button
                  type="button"
                  onClick={() => {
                    setShowCadastro(true);
                  }}
                  variant="primary"
                  size="sm"
                >
                  Adicionar
                </Button>
              </div>
              {certificado && certificado.length > 0 ? (
                <table
                  className="table table-hover table-sm table-font-small"
                  style={{ borderWidth: '0px' }}
                >
                  <thead>
                    <tr className="bg-light text-dark">
                      <th style={{ width: '115px' }}>Validade</th>
                      <th>Arquivo</th>
                      <th style={{ width: '60px' }}>&nbsp;</th>
                    </tr>
                  </thead>
                  <tbody>
                    {certificado.map((cert: ICertificados) => (
                      <tr
                        key={`certificado-${cert.CerCodigo}`}
                        style={{
                          fontSize: '.7rem',
                          height: '30px',
                        }}
                      >
                        <td>{cert.dataValidadeFormatada}</td>
                        <td>{cert.CerArquivo}</td>
                        <td>
                          <Button
                            type="button"
                            size="sm"
                            title={cert.CerCodigo.toString() || ''}
                            variant="outline-primary"
                            onClick={() => handleDelete(cert.CerCodigo)}
                          >
                            <FaTrashAlt size={16} />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p>Nenhum certificado cadastrado</p>
              )}
            </Col>
          </Row>
        )}
      </ContainerDados>
    </>
  );
};

export default Certificado;
